/**
 * Order Calculator for Manager Panel
 * Backend-Driven Calculation System with Per-Brand Support
 *
 * ⚠️ CRITICAL: All calculations happen on backend (PHP), NOT frontend (JavaScript)
 * Frontend ONLY collects input parameters and displays results from server
 *
 * H.2.b/c contract: money-поля передаются и получаются в integer cents.
 * Внутри калькулятора brand.fields.<money> хранятся в cents. Input value в Blade —
 * EUR-decimal для UX; на чтении × 100, на отображении / 100.
 */

// Money-поля в API контракте (int cents).
const MONEY_FIELDS = new Set([
    'pick_up', 'transport_tarif', 'packaging', 'certificate', 'other_shipping',
    'insurance_amount', 'transport', 'customs_amount', 'total_shipping',
    'invoice_price', 'complete', 'other_final', 'subtotal', 'trade_discount',
    'total_with_discount', 'vat_amount', 'final_total',
]);

// Editable money-input ID-suffix → key field. Для них read-конверсия EUR-decimal × 100.
const EDITABLE_MONEY_FIELDS = new Set([
    'pick_up', 'transport_tarif', 'packaging', 'certificate', 'other_shipping',
    'insurance_amount', 'trade_discount',
]);

const eurToCents = (eur) => {
    const n = parseFloat(eur);
    if (!isFinite(n) || isNaN(n)) return 0;
    return Math.round(n * 100);
};

const centsToEur = (cents) => {
    const n = typeof cents === 'number' ? cents : parseInt(cents, 10);
    return isFinite(n) ? n / 100 : 0;
};

class OrderCalculator {
    constructor(orderId) {
        this.orderId = orderId;
        this.brands = {}; // Store data per brand
        this.debounceTimeout = null;
        this.isCalculating = false;
        this.qtyChanged = false;
        this.productQuantities = [];
        this.focusedElement = null;
        this.init();
    }

    init() {
        this.collectBrands();
        this.bindEvents();
    }

    collectBrands() {
        document.querySelectorAll('.adm_req_brand_item[data-brand-name]').forEach(brandEl => {
            const brandName = brandEl.dataset.brandName;
            const brandSlug = brandEl.dataset.brandSlug;

            this.brands[brandName] = {
                slug: brandSlug,
                element: brandEl,
                fields: this.getDefaultFields(),
            };

            this.loadBrandData(brandName);
        });
    }

    /**
     * Defaults в cents для money-полей и в float для percent/volume.
     */
    getDefaultFields() {
        return {
            pick_up: 0,             // cents
            total_volume: 0,        // m³
            volume_adjustment: 0.1, // coefficient
            transport_tarif: 50000, // cents (€500/m³)
            insurance_percent: 1.5,
            packaging: 0,           // cents
            certificate: 10000,     // cents (€100)
            other_shipping: 0,
            customs_percent: 0,
            trade_discount: 0,      // cents
            vat_percent: 20,
            calculation_comments: '',
            // Calculated (cents):
            volume_adjusted: 0,
            transport: 0,
            insurance_amount: 0,
            customs_amount: 0,
            total_shipping: 0,
            invoice_price: 0,
            complete: 0,
            other_final: 0,
            subtotal: 0,
            total_with_discount: 0,
            vat_amount: 0,
            final_total: 0,
        };
    }

    /**
     * Load initial values from inputs. Money-инпуты содержат EUR-decimal — × 100.
     */
    loadBrandData(brandName) {
        const brand = this.brands[brandName];
        if (!brand) return;
        const slug = brand.slug;

        const editableFields = [
            'pick_up', 'total_volume', 'volume_adjustment', 'transport_tarif', 'insurance_percent',
            'insurance_amount', 'packaging', 'certificate', 'other_shipping', 'customs_percent',
            'trade_discount', 'vat_percent',
        ];

        editableFields.forEach(field => {
            const element = document.getElementById(`calc_${field}_${slug}`);
            if (!element) return;
            const raw = parseFloat(element.value);
            if (isNaN(raw)) return;
            brand.fields[field] = EDITABLE_MONEY_FIELDS.has(field) ? eurToCents(raw) : raw;
        });

        // trade_discount — не редактируемый инпут, а display-значение (переносится из
        // расчёта заявки). Берём сохранённые cents из data-cents, иначе при любом
        // пересчёте в payload уйдёт 0 и скидка слетит (total_with_discount = subtotal).
        const tradeDiscountEl = document.getElementById(`calc_trade_discount_display_${slug}`);
        if (tradeDiscountEl && tradeDiscountEl.dataset.cents !== undefined && tradeDiscountEl.dataset.cents !== '') {
            brand.fields.trade_discount = parseInt(tradeDiscountEl.dataset.cents, 10) || 0;
        }

        const commentsEl = document.getElementById(`calculation_comments_${slug}`);
        if (commentsEl) {
            brand.fields.calculation_comments = commentsEl.value || '';
        }
    }

    bindEvents() {
        document.querySelectorAll('.brand-calc-input').forEach(input => {
            input.addEventListener('focus', () => {
                this.focusedElement = input;
                input.select();
            });

            input.addEventListener('blur', () => {
                if (this.focusedElement === input) {
                    this.focusedElement = null;
                    if (input.type === 'number' && input.value !== '') {
                        const v = parseFloat(input.value);
                        if (!isNaN(v)) {
                            input.value = v.toFixed(2);
                        }
                    }
                }
            });

            input.addEventListener('input', () => {
                const brandEl = input.closest('.adm_req_brand_item[data-brand-name]');
                if (!brandEl) return;
                const brandName = brandEl.dataset.brandName;
                const field = input.dataset.field;
                if (!field || !this.brands[brandName]) return;

                if (field === 'calculation_comments') {
                    this.brands[brandName].fields[field] = input.value || '';
                } else {
                    const raw = parseFloat(input.value) || 0;
                    this.brands[brandName].fields[field] = EDITABLE_MONEY_FIELDS.has(field)
                        ? eurToCents(raw)
                        : raw;
                }

                if (field === 'insurance_percent') {
                    delete this.brands[brandName].fields.insurance_amount;
                }

                if (input.dataset.field !== 'calculation_comments') {
                    this.requestCalculation();
                }
            });
        });

        document.querySelectorAll('.brand-save-btn').forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                this.saveBrandCalculation(button.dataset.brandName);
            });
        });

        document.querySelectorAll('.brand-approve-btn').forEach(button => {
            button.addEventListener('click', (e) => {
                e.preventDefault();
                this.approveBrandCalculation(button.dataset.brandName);
            });
        });

        const saveAllButton = document.getElementById('save-all-btn');
        if (saveAllButton) {
            saveAllButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.saveAllCalculations();
            });
        }

        const approveAllButton = document.getElementById('approve-all-btn');
        if (approveAllButton) {
            approveAllButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.approveAllCalculations();
            });
        }
    }

    requestCalculation() {
        if (this.debounceTimeout) clearTimeout(this.debounceTimeout);
        this.debounceTimeout = setTimeout(() => this.sendCalculationRequest(), 300);
    }

    calculateAll() {
        this.qtyChanged = true;
        this.collectProductQuantities();
        this.requestCalculation();
    }

    collectProductQuantities() {
        this.productQuantities = [];
        document.querySelectorAll('.adm_vidget_count').forEach(widget => {
            const productId = widget.dataset.productId;
            const qty = parseInt(widget.querySelector('.adm_vidget_value').textContent) || 1;
            if (productId) this.productQuantities.push({ product_id: productId, quantity: qty });
        });
    }

    collectProductComments() {
        this.productComments = [];
        document.querySelectorAll('textarea[name^="comments_calculation"]').forEach(textarea => {
            const row = textarea.closest('tr');
            const widget = row ? row.querySelector('.adm_vidget_count') : null;
            const productId = widget ? widget.dataset.productId : null;
            if (productId) {
                this.productComments.push({ product_id: productId, comment: textarea.value || '' });
            }
        });
    }

    collectProductExpectedDates() {
        this.productExpectedDates = [];
        document.querySelectorAll('.product-expected-date').forEach(input => {
            const productId = input.dataset.productId;
            const dateValue = input.value || null;
            if (productId) {
                this.productExpectedDates.push({ product_id: productId, expected_delivery_date: dateValue });
            }
        });
    }

    sendCalculationRequest() {
        if (this.isCalculating) return;
        this.isCalculating = true;

        const brandsData = {};
        for (const brandName in this.brands) {
            brandsData[brandName] = this.brands[brandName].fields;
        }

        const data = {
            order_id: this.orderId,
            brands: brandsData,
            product_quantities: this.qtyChanged ? (this.productQuantities || []) : [],
        };
        this.qtyChanged = false;

        fetch('/manager/order/calculate', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
            },
            body: JSON.stringify(data),
        })
        .then(response => response.json())
        .then(result => {
            this.isCalculating = false;
            if (result.success && result.data) {
                this.updateFromServer(result.data);
                this.updateDisplay();
            } else {
                console.error('Calculation failed:', result.message);
            }
        })
        .catch(error => {
            this.isCalculating = false;
            console.error('Calculation request error:', error);
        });
    }

    updateFromServer(serverData) {
        if (serverData.brands) {
            for (const brandName in serverData.brands) {
                if (!this.brands[brandName]) continue;
                const brandData = serverData.brands[brandName];
                // Money-поля в response — int cents, кладём как есть.
                Object.assign(this.brands[brandName].fields, {
                    total_volume: brandData.total_volume || 0,
                    volume_adjusted: brandData.volume_adjusted || 0,
                    transport: brandData.transport || 0,
                    insurance_amount: brandData.insurance_amount || 0,
                    customs_amount: brandData.customs_amount || 0,
                    total_shipping: brandData.total_shipping || 0,
                    invoice_price: brandData.invoice_price || 0,
                    complete: brandData.complete || 0,
                    other_final: brandData.other_final || 0,
                    subtotal: brandData.subtotal || 0,
                    total_with_discount: brandData.total_with_discount || 0,
                    vat_amount: brandData.vat_amount || 0,
                    final_total: brandData.final_total || 0,
                });
                if (brandData.products) this.brands[brandName].products = brandData.products;
                if (brandData.expected_date) this.brands[brandName].expected_date = brandData.expected_date;
            }
        }
        if (serverData.totals) this.totals = serverData.totals;
    }

    updateDisplay() {
        for (const brandName in this.brands) {
            this.updateBrandDisplay(brandName);
            this.updateProductsDisplay(brandName);
        }
        this.updateTotalsDisplay();
    }

    updateProductsDisplay(brandName) {
        const brand = this.brands[brandName];
        if (!brand || !brand.products) return;

        for (const productId in brand.products) {
            const productData = brand.products[productId];
            // Все money-поля productData — cents.
            this.setMoneyText(`product_total_${productId}`, productData.total_amount);

            const setAmountSpan = (elId, cents) => {
                const el = document.getElementById(elId);
                if (!el) return;
                const amountEl = el.querySelector('.amount');
                if (amountEl) amountEl.textContent = this.formatNumber(centsToEur(cents));
            };
            setAmountSpan(`product_shipping_${productId}`, productData.total_shipping);
            setAmountSpan(`product_vat_${productId}`, productData.total_vat);
            setAmountSpan(`product_final_${productId}`, productData.total_final);
        }
    }

    updateBrandDisplay(brandName) {
        const brand = this.brands[brandName];
        if (!brand) return;
        const slug = brand.slug;
        const f = brand.fields;

        // Volume — float m³, не money.
        this.setInputValueRaw(`calc_total_volume_${slug}`, f.total_volume);

        // Money displays / inputs — конверсия cents → EUR.
        this.setMoneyText(`shipping_pickup_${slug}`, f.pick_up);
        this.setElementTextRaw(`calc_volume_adjusted_display_${slug}`, this.formatNumber(f.volume_adjusted));
        this.setMoneyText(`shipping_transport_${slug}`, f.transport_tarif);
        this.setMoneyInputValue(`calc_insurance_amount_${slug}`, f.insurance_amount);
        this.setMoneyText(`shipping_packaging_${slug}`, f.packaging);
        this.setMoneyText(`shipping_certificate_${slug}`, f.certificate);
        this.setMoneyText(`shipping_other_${slug}`, f.other_shipping);
        this.setMoneyText(`shipping_customs_${slug}`, f.customs_amount);
        this.setMoneyText(`shipping_total_${slug}`, f.total_shipping);

        this.setMoneyText(`calc_invoice_price_display_${slug}`, f.invoice_price);
        this.setMoneyText(`calc_complete_display_${slug}`, f.complete);
        this.setMoneyText(`calc_other_final_display_${slug}`, f.other_final);
        this.setMoneyText(`summary_shipping_${slug}`, f.total_shipping);
        this.setMoneyText(`calc_subtotal_display_${slug}`, f.subtotal);
        this.setMoneyText(`calc_trade_discount_display_${slug}`, f.trade_discount);
        this.setMoneyText(`calc_total_with_discount_display_${slug}`, f.total_with_discount);
        this.setMoneyText(`calc_vat_amount_display_${slug}`, f.vat_amount);
        this.setMoneyText(`calc_final_total_display_${slug}`, f.final_total);

        if (brand.expected_date) {
            this.setElementTextRaw(`brand_expected_date_${slug}`, this.formatDate(brand.expected_date));
        }
    }

    updateTotalsDisplay() {
        if (!this.totals) return;
        const totalQty = this.calculateTotalQuantity();
        this.setElementTextRaw('total_qty_items', totalQty);

        // totals.* в response — cents.
        const fmt = (cents) => `€ ${this.formatNumber(centsToEur(cents))}`;
        this.setElementTextRaw('total_subtotal_amount', fmt(this.totals.subtotal));
        this.setElementTextRaw('total_shipping_amount', fmt(this.totals.total_shipping));
        this.setElementTextRaw('total_vat_amount', fmt(this.totals.vat_amount));
        this.setElementTextRaw('total_final_amount', fmt(this.totals.final_total));

        if (this.totals.expected_date) {
            const expectedDateInput = document.getElementById('order_expected_date');
            if (expectedDateInput) expectedDateInput.value = this.totals.expected_date;
        }
    }

    /**
     * Текстовое отображение money: cents → EUR-decimal.
     */
    setMoneyText(id, cents) {
        const el = document.getElementById(id);
        if (el) el.textContent = this.formatNumber(centsToEur(cents));
    }

    setElementTextRaw(id, text) {
        const el = document.getElementById(id);
        if (el) el.textContent = text;
    }

    setMoneyInputValue(id, cents) {
        const el = document.getElementById(id);
        if (!el) return;
        if (this.focusedElement === el) return;
        el.value = centsToEur(cents).toFixed(2);
    }

    setInputValueRaw(id, value) {
        const el = document.getElementById(id);
        if (!el) return;
        if (this.focusedElement === el) return;
        el.value = this.formatNumber(value).replace(/,/g, '');
    }

    calculateTotalQuantity() {
        let totalQty = 0;
        document.querySelectorAll('.adm_vidget_value').forEach(element => {
            totalQty += parseInt(element.textContent) || 0;
        });
        return totalQty;
    }

    formatNumber(number) {
        if (typeof number !== 'number') number = parseFloat(number) || 0;
        return number.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    }

    formatDate(dateStr) {
        // Единый формат дат сайта — d.m.Y (см. config('app.date_format')).
        if (!dateStr) return 'DD.MM.YYYY';
        const parts = dateStr.split('-');
        if (parts.length === 3) return `${parts[2]}.${parts[1]}.${parts[0]}`;
        return dateStr;
    }

    saveBrandCalculation(brandName) {
        this.saveToBackend([brandName]);
    }

    saveAllCalculations() {
        this.saveToBackend(Object.keys(this.brands));
    }

    saveToBackend(brandNames) {
        const brandsData = {};
        brandNames.forEach(name => {
            if (this.brands[name]) brandsData[name] = this.brands[name].fields;
        });

        this.collectProductComments();
        this.collectProductExpectedDates();

        const data = {
            order_id: this.orderId,
            brands: brandsData,
            product_comments: this.productComments || [],
            product_expected_dates: this.productExpectedDates || [],
        };

        fetch('/manager/order/save-calculation', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
            },
            body: JSON.stringify(data),
        })
        .then(response => response.json())
        .then(result => {
            if (result.success) {
                if (typeof showToast === 'function') {
                    showToast('success', result.message || (typeof TOAST_MESSAGES !== 'undefined' ? TOAST_MESSAGES.ORDER.SAVED : 'Saved'));
                }
            } else {
                if (typeof showToast === 'function') {
                    showToast('error', result.message || (typeof TOAST_MESSAGES !== 'undefined' ? TOAST_MESSAGES.ORDER.ERROR_SAVING : 'Error saving'));
                }
            }
        })
        .catch(error => {
            console.error('Save error:', error);
            if (typeof showToast === 'function') {
                showToast('error', typeof TOAST_MESSAGES !== 'undefined' ? TOAST_MESSAGES.ORDER.ERROR_SAVING : 'Error saving');
            }
        });
    }

    approveBrandCalculation(brandName) {
        const onConfirm = () => this.approveToBackend([brandName]);
        if (typeof showAdmConfirmation === 'function') {
            showAdmConfirmation('Are you sure you want to send the calculation results?', {
                confirmText: 'Send', cancelText: 'Cancel',
            }).then((confirmed) => { if (confirmed) onConfirm(); });
        } else if (confirm('Are you sure you want to send the calculation results?')) {
            onConfirm();
        }
    }

    approveAllCalculations() {
        const selectedBrands = [];
        document.querySelectorAll('.brand-select-checkbox:checked').forEach(cb => {
            selectedBrands.push(cb.dataset.brandName);
        });

        if (selectedBrands.length === 0) {
            if (typeof showToast === 'function' && typeof TOAST_MESSAGES !== 'undefined') {
                showToast('warning', TOAST_MESSAGES.ORDER.SELECT_BRAND_REQUIRED);
            }
            return;
        }

        const onConfirm = () => this.approveToBackend(selectedBrands);
        if (typeof showAdmConfirmation === 'function') {
            showAdmConfirmation('Are you sure you want to send the calculation results?', {
                confirmText: 'Send', cancelText: 'Cancel',
            }).then((confirmed) => { if (confirmed) onConfirm(); });
        } else if (confirm('Are you sure you want to send the calculation results?')) {
            onConfirm();
        }
    }

    approveToBackend(brandNames) {
        const brandsData = {};
        brandNames.forEach(name => {
            if (this.brands[name]) brandsData[name] = this.brands[name].fields;
        });

        this.collectProductComments();
        this.collectProductExpectedDates();

        const data = {
            order_id: this.orderId,
            brand_names: brandNames,
            brands: brandsData,
            product_comments: this.productComments || [],
            product_expected_dates: this.productExpectedDates || [],
        };

        fetch('/manager/order/approve', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content || '',
            },
            body: JSON.stringify(data),
        })
        .then(response => response.json())
        .then(result => {
            if (result.success) {
                if (typeof showToast === 'function') {
                    showToast('success', result.message || (typeof TOAST_MESSAGES !== 'undefined' ? TOAST_MESSAGES.ORDER.APPROVED : 'Approved'));
                }
                setTimeout(() => window.location.reload(), 2000);
            } else {
                if (typeof showToast === 'function') {
                    showToast('error', result.message || (typeof TOAST_MESSAGES !== 'undefined' ? TOAST_MESSAGES.ORDER.ERROR_APPROVING : 'Error approving'));
                }
            }
        })
        .catch(error => {
            console.error('Approve error:', error);
            if (typeof showToast === 'function') {
                showToast('error', typeof TOAST_MESSAGES !== 'undefined' ? TOAST_MESSAGES.ORDER.ERROR_APPROVING : 'Error approving');
            }
        });
    }
}

document.addEventListener('DOMContentLoaded', function() {
    const orderIdElement = document.querySelector('[data-order-id]');
    if (orderIdElement) {
        const orderId = orderIdElement.dataset.orderId;
        window.orderCalculator = new OrderCalculator(orderId);
    }
});
